import React, { useState, useEffect } from "react";
import { Table } from "react-bootstrap";
import { MdOutlineInfo } from "react-icons/md";
import { useIntl } from "react-intl";
import Config from "../config/Config";
import getLocalizedText from "./Utils";
import AasDetailsModal from "./modals/AasDetailsModal";
import { fetchSubmodelElement } from "../services/DppManagerService";
import LoadingDots from "./LoadingDots";
import "../css/DppProductInfo.css";

const { mainColor } = Config.appearance;
const ITEM_STYLE = { backgroundColor: "transparent", color: "#333333", fontSize: "16px" };
const LABEL_COLUMN_STYLE = { backgroundColor: "transparent", width: "150px", padding: "3px 6px", color: "#333", fontSize: "16px" };
const VALUE_COLUMN_STYLE = { backgroundColor: "transparent", padding: "3px 6px", color: mainColor, fontSize: "16px" };
const LOADING_CONTAINER_STYLE = {
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  minHeight: "230px",
  width: "100%",
};

// Define propertyMap for info
const PROPERTY_MAP = [
  { submodelIdShort: "PublicInformation", path: "ManufacturerProductType", key: "manufacturerProductType" },
  { submodelIdShort: "PublicInformation", path: "SerialNumber", key: "serialNumber" },
  { submodelIdShort: "PublicInformation", path: "FirmwareVersion", key: "firmwareVersion" },
  { submodelIdShort: "PublicInformation", path: "HardwareVersion", key: "hardwareVersion" },
  { submodelIdShort: "PublicInformation", path: "YearOfConstruction", key: "yearOfConstruction" },
];

// Map property keys to translation IDs
const PROPERTY_LABEL_IDS = {
  manufacturerProductType: "product_type",
  serialNumber: "serial_number",
  firmwareVersion: "firmware_version",
  hardwareVersion: "hardware_version",
  yearOfConstruction: "construction_year",
};

function DppProductInfo({
  assetAdministrationShell,
  submodelDescriptors,
}) {

  const intl = useIntl();
  const [showModal, setShowModal] = useState(false);
  const [infoData, setInfoData] = useState({});
  const [loading, setLoading] = useState(false);

  const handleShow = () => setShowModal(true);
  const handleClose = () => setShowModal(false);

  const { description, displayName, idShort } = assetAdministrationShell || {};
  const noDescriptionAvailable = intl.formatMessage({ id: "no_description_available" });
  const displayNameLocalized = getLocalizedText(displayName, idShort, intl.locale);
  const descriptionLocalized = getLocalizedText(description, noDescriptionAvailable, intl.locale);

  useEffect(() => {
    let mounted = true;
    const controller = new AbortController();

    const loadInfoData = async () => {
      if (!submodelDescriptors?.length) return;

      setLoading(true);

      const fetchPromises = PROPERTY_MAP.map(async ({ submodelIdShort, path, key }) => {
        const descriptor = submodelDescriptors.find(d => d.idShort === submodelIdShort);
        if (!descriptor) return { key, value: null };

        try {
          const element = await fetchSubmodelElement(descriptor, path, { signal: controller.signal });
          const rawValue = element?.value ??
            element?.file?.path ??
            element?.url ??
            null;
          return { key, value: getLocalizedText(rawValue) };
        } catch (error) {
          if (error.name === 'AbortError') return { key, value: null };
          console.warn(`Failed to fetch ${path} from ${submodelIdShort}:`, error);
          return { key, value: null }; // Don't break everything because one failed
        }
      });

      try {
        const results = await Promise.all(fetchPromises);

        if (mounted) {
          const dataObj = results.reduce((acc, { key, value }) => {
            if (value !== null) acc[key] = value;
            return acc;
          }, {});
          setInfoData(dataObj);
        }
      } catch (err) {
        console.error("Critical failure loading product info:", err);
      } finally {
        if (mounted) setLoading(false);
      }
    };

    loadInfoData();

    return () => {
      mounted = false;
      controller.abort(); // Clean up pending fetches
    };
  }, [submodelDescriptors]);

  if (loading) {
    return (
      <div style={LOADING_CONTAINER_STYLE}>
        <LoadingDots color={mainColor} />
      </div>
    );
  }

  return (
    <div style={{ margin: 0, padding: 0, backgroundColor: "transparent" }}>
      <h5
        className="d-flex align-items-center ms-2 mb-3"
        style={{ color: "#333333", fontWeight: 600, fontSize: "1.2rem", letterSpacing: "0.3px" }}
      >
        {displayNameLocalized}
        <MdOutlineInfo
          size={20}
          style={{
            cursor: "pointer",
            color: mainColor,
            position: "relative",
            top: "1px",
            left: "8px",
          }}
          onClick={handleShow}
          aria-label="Show details"
        />
      </h5>

      <AasDetailsModal
        show={showModal}
        handleClose={handleClose}
        assetInfo={assetAdministrationShell}
      />

      <Table borderless className="ms-1" style={{ margin: 0, padding: 0, maxWidth: "400px" }}>
        <tbody style={{ backgroundColor: "transparent" }}>
          <tr key="description" style={ITEM_STYLE}>
            <td style={LABEL_COLUMN_STYLE}>
              {intl.formatMessage({ id: "description" })}
            </td>
            <td style={VALUE_COLUMN_STYLE}>{descriptionLocalized}</td>
          </tr>

          {Object.entries(infoData).map(([key, value]) => (
            value && value !== "N/A" && (
              <tr key={key} style={ITEM_STYLE}>
                <td style={LABEL_COLUMN_STYLE}>
                  {intl.formatMessage({
                    id: PROPERTY_LABEL_IDS[key],
                    defaultMessage: key, // Fallback to key if translation is missing
                  })}
                </td>
                <td style={VALUE_COLUMN_STYLE}>{value}</td>
              </tr>
            )
          ))}
        </tbody>
      </Table>
    </div>
  );
}

export default DppProductInfo;